import { type PDFDocumentProxy, type PDFPageProxy } from 'pdfjs-dist';

export class PDFPagesCache {
  private readonly pagesPromises = new Map<number, Promise<PDFPageProxy>>();
  private readonly pages = new Map<number, PDFPageProxy>();
  private readonly onPagesLoaded: () => void;
  private pdfDocument: PDFDocumentProxy;

  constructor(pdfDocument: PDFDocumentProxy, onPagesLoaded: () => void) {
    this.pdfDocument = pdfDocument;
    this.onPagesLoaded = onPagesLoaded;

    this.generatePages();
  }

  private loadPage = async (pageNumber: number) => {
    const pagePromise = this.pagesPromises.get(pageNumber);
    if (pagePromise) {
      return pagePromise
        .then((pdfPage) => {
          this.pagesPromises.delete(pageNumber);
          this.pages.set(pageNumber, pdfPage);
          return pdfPage;
        })
        .catch((error) => {
          console.error(`Error while loading page ${pageNumber}`, error);
          return null;
        });
    }
  };

  private generatePages = () => {
    for (let i = 0; i < this.pdfDocument.numPages; i++) {
      if (!this.pagesPromises.has(i + 1)) {
        const promise = this.pdfDocument.getPage(i + 1);
        this.pagesPromises.set(i + 1, promise);
      }
    }

    for (let i = 0; i < this.pdfDocument.numPages; i++) {
      if (!this.pages.has(i + 1)) {
        this.loadPage(i + 1).then(() => {
          if (this.pages.size === this.pdfDocument.numPages) {
            this.onPagesLoaded();
          }
        });
      }
    }
  };

  getPage = async (pageNumber: number) => {
    const page = this.pages.get(pageNumber);
    if (!page) {
      return this.loadPage(pageNumber);
    }
    return Promise.resolve(page);
  };

  getLoadedPage = (pageNumber: number) => this.pages.get(pageNumber);
}
